import React, { useState, useRef, useCallback, useEffect, Component } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Camera, CreditCard, User, CheckCircle2, XCircle,
  Loader2, ArrowRight, ArrowLeft, RotateCcw, Shield, ScanFace, AlertTriangle, Clock,
  ZoomIn, RotateCw, Upload
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// No top-level imports of react-easy-crop — loaded dynamically only when needed

// Error Boundary: atrapa errores y muestra fallback en vez de pantalla blanca
class PhotoErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMsg: '' };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, errorMsg: String(error?.message || error || 'Unknown') };
  }
  componentDidCatch(error, info) {
    console.error('[PhotoErrorBoundary]', error, info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center gap-3 p-4">
          <p className="text-sm text-amber-300 text-center">La camara no es compatible con este dispositivo.</p>
          <p className="text-xs text-slate-400 text-center">Usa el boton para subir una foto desde tu galeria.</p>
          <label className="w-full max-w-xs cursor-pointer">
            <div className="flex items-center justify-center gap-2 h-11 rounded-md bg-emerald-600 text-white font-medium text-sm">
              <Upload className="w-5 h-5" /> Subir Foto
            </div>
            <input type="file" accept="image/*" className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = (ev) => this.props.onAccept(ev.target.result);
                reader.readAsDataURL(file);
              }} />
          </label>
        </div>
      );
    }
    return this.props.children;
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = 'kinosis_enrolamiento_state';

const formatRut = (value) => {
  let rut = value.replace(/[^0-9kK]/g, '').toUpperCase();
  if (rut.length === 0) return '';
  let dv = rut.slice(-1);
  let cuerpo = rut.slice(0, -1);
  let f = '', c = 0;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    f = cuerpo[i] + f; c++;
    if (c === 3 && i !== 0) { f = '.' + f; c = 0; }
  }
  if (cuerpo.length === 0) return dv;
  return f + '-' + dv;
};

const validarRut = (rut) => {
  const limpio = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  if (limpio.length < 8 || limpio.length > 9) return false;
  const cuerpo = limpio.slice(0, -1), dv = limpio.slice(-1);
  let suma = 0, m = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i]) * m;
    m = m === 7 ? 2 : m + 1;
  }
  const r = suma % 11;
  return dv === (r === 0 ? '0' : r === 1 ? 'K' : String(11 - r));
};

const normalizarRut = (rut) => rut.replace(/[^0-9kK]/g, '').toUpperCase();

// Formato de fecha para mostrar al usuario: dd-MM-yyyy
const formatDateDisplay = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3 && parts[0].length === 4) {
    return `${parts[2]}-${parts[1]}-${parts[0]}`;
  }
  return dateStr;
};

function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (rotation === 0) {
        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
      } else {
        const rad = (rotation * Math.PI) / 180;
        const sin = Math.abs(Math.sin(rad));
        const cos = Math.abs(Math.cos(rad));
        const bW = image.width * cos + image.height * sin;
        const bH = image.width * sin + image.height * cos;

        const tmpCanvas = document.createElement('canvas');
        tmpCanvas.width = bW;
        tmpCanvas.height = bH;
        const tmpCtx = tmpCanvas.getContext('2d');
        tmpCtx.translate(bW / 2, bH / 2);
        tmpCtx.rotate(rad);
        tmpCtx.drawImage(image, -image.width / 2, -image.height / 2);

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;
        ctx.drawImage(tmpCanvas, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
      }

      resolve(canvas.toDataURL('image/jpeg', 0.95));
    };
    image.src = imageSrc;
  });
}

// ========== PHOTO CAPTURE WITH INLINE CAMERA (getUserMedia) ==========
// react-easy-crop is loaded DYNAMICALLY only after a photo is captured, never at module level
function PhotoCapture({ onAccept, finalImage, mode, onRetake }) {
  const videoRef = useRef(null);
  const fileInputRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [rawImage, setRawImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  // Dynamic crop module: loaded on demand
  const [CropperComp, setCropperComp] = useState(null);
  const [cropLoadFailed, setCropLoadFailed] = useState(false);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  // Load react-easy-crop dynamically when rawImage is set
  useEffect(() => {
    if (!rawImage || CropperComp || cropLoadFailed) return;
    let cancelled = false;
    import('react-easy-crop')
      .then((mod) => {
        if (!cancelled) setCropperComp(() => mod.default || mod.Cropper);
      })
      .catch((err) => {
        console.warn('react-easy-crop load failed:', err);
        if (!cancelled) setCropLoadFailed(true);
      });
    return () => { cancelled = true; };
  }, [rawImage, CropperComp, cropLoadFailed]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
    };
  }, []);

  // Check if camera API is available
  const hasCameraAPI = typeof navigator !== 'undefined' && navigator.mediaDevices && typeof navigator.mediaDevices.getUserMedia === 'function';

  // Start inline camera
  const startCamera = async () => {
    setCameraError(null);
    if (!hasCameraAPI) {
      setCameraError('Tu navegador no soporta camara inline. Usa "Subir foto".');
      return;
    }
    try {
      const facingMode = mode === 'selfie' ? 'user' : 'environment';
      let mediaStream;
      try {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: mode === 'cedula' ? { exact: 'environment' } : 'user',
            width: { ideal: 1920 },
            height: { ideal: 1440 }
          },
          audio: false
        });
      } catch {
        mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode, width: { ideal: 1280 }, height: { ideal: 960 } },
          audio: false
        });
      }
      streamRef.current = mediaStream;
      setCameraActive(true);
    } catch (err) {
      console.error('Camera error:', err);
      setCameraError(err.name === 'NotAllowedError'
        ? 'Permiso de camara denegado. Usa "Subir foto" o permite el acceso en la configuracion del navegador.'
        : 'No se pudo abrir la camara. Usa "Subir foto".');
    }
  };

  // Attach stream to video element when camera becomes active
  useEffect(() => {
    if (cameraActive && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [cameraActive]);

  // Capture photo from live video
  const captureFromVideo = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
    stopCamera();
    setRawImage(dataUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  // File upload fallback
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten imagenes'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setRawImage(ev.target.result);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
    };
    reader.onerror = () => toast.error('Error al leer la imagen.');
    reader.readAsDataURL(file);
  };

  const openFilePicker = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleAccept = async () => {
    if (CropperComp && croppedAreaPixels && rawImage) {
      const cropped = await getCroppedImg(rawImage, croppedAreaPixels, rotation);
      setRawImage(null);
      setRotation(0);
      onAccept(cropped);
    } else {
      // No cropper available — accept raw image
      const img = rawImage;
      setRawImage(null);
      onAccept(img);
    }
  };

  const handleRetry = () => {
    setRawImage(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  // === ACCEPTED IMAGE ===
  if (finalImage && !rawImage && !cameraActive) {
    return (
      <div className="flex flex-col items-center gap-2">
        <div className="relative w-full max-w-[160px] rounded-lg overflow-hidden border-2 border-emerald-500">
          <img src={finalImage} alt="Captura" className="w-full" />
          <Badge className="absolute top-1 right-1 bg-emerald-500 text-white text-[10px] px-1.5 py-0.5" data-testid="photo-captured-badge">
            <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> OK
          </Badge>
        </div>
        <button onClick={() => { stopCamera(); onRetake(); }} data-testid="retake-photo-btn"
          className="text-xs text-slate-400 underline underline-offset-2 hover:text-slate-300">
          Tomar otra vez
        </button>
      </div>
    );
  }

  // === CROP VIEW (cropper loaded dynamically) ===
  if (rawImage) {
    // Fallback: cropper not loaded yet or failed — show simple preview
    if (!CropperComp) {
      return (
        <div className="flex flex-col items-center gap-3">
          <p className="text-xs text-slate-400 text-center">Vista previa</p>
          <img src={rawImage} alt="Preview" className="w-full max-h-60 object-contain rounded-xl border border-slate-700" />
          {!cropLoadFailed && (
            <p className="text-xs text-slate-500 text-center">Cargando editor...</p>
          )}
          <div className="flex gap-3 w-full">
            <Button variant="outline" onClick={handleRetry}
              className="flex-1 border-slate-600 text-slate-300 gap-1 h-10" data-testid="crop-retry-btn">
              <RotateCcw className="w-4 h-4" /> Reintentar
            </Button>
            <Button onClick={() => { onAccept(rawImage); setRawImage(null); }}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-10" data-testid="crop-accept-btn">
              <CheckCircle2 className="w-4 h-4" /> Aceptar
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-xs text-slate-400 text-center">
          {mode === 'selfie' ? 'Centra tu rostro' : 'Centra la cedula completa'}
        </p>
        <div className="relative w-full rounded-xl overflow-hidden bg-black"
          style={{ height: mode === 'selfie' ? 220 : 260 }}>
          <CropperComp
            image={rawImage}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={mode === 'selfie' ? 3 / 4 : 8.56 / 5.4}
            cropShape={mode === 'selfie' ? 'round' : 'rect'}
            cropSize={mode === 'cedula' ? { width: 300, height: 190 } : undefined}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            showGrid={false}
          />
        </div>
        <div className="flex items-center gap-3 w-full px-2">
          <ZoomIn className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input type="range" min="1" max="3" step="0.1" value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))} className="flex-1 accent-emerald-500" />
          {mode === 'cedula' && (
            <button onClick={() => setRotation((r) => (r + 90) % 360)}
              className="p-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors flex-shrink-0"
              title="Rotar 90" data-testid="rotate-btn">
              <RotateCw className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex gap-3 w-full">
          <Button variant="outline" onClick={handleRetry}
            className="flex-1 border-slate-600 text-slate-300 gap-1 h-10" data-testid="crop-retry-btn">
            <RotateCcw className="w-4 h-4" /> Reintentar
          </Button>
          <Button onClick={handleAccept}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-1 h-10" data-testid="crop-accept-btn">
            <CheckCircle2 className="w-4 h-4" /> Aceptar
          </Button>
        </div>
      </div>
    );
  }

  // === LIVE CAMERA VIEW ===
  if (cameraActive) {
    return (
      <div className="flex flex-col items-center gap-3">
        <p className="text-xs text-slate-400 text-center">
          {mode === 'selfie' ? 'Mira a la camara y captura' : 'Enfoca la cedula completa'}
        </p>
        <div className="relative w-full rounded-xl overflow-hidden bg-black"
          style={{ height: mode === 'selfie' ? 280 : 240 }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={mode === 'selfie' ? { transform: 'scaleX(-1)' } : undefined}
          />
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            {mode === 'selfie' ? (
              <div className="w-36 h-48 border-2 border-emerald-400/40 rounded-full" />
            ) : (
              <div className="w-[80%] h-[65%] border-2 border-amber-400/40 rounded-lg" />
            )}
          </div>
        </div>
        <div className="flex gap-3 w-full">
          <Button variant="outline" onClick={stopCamera}
            className="flex-1 border-slate-600 text-slate-300 h-11" data-testid="camera-cancel-btn">
            Cancelar
          </Button>
          <Button onClick={captureFromVideo}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-11" data-testid="camera-capture-btn">
            <Camera className="w-5 h-5" /> Capturar
          </Button>
        </div>
      </div>
    );
  }

  // === INITIAL: CAMERA + FALLBACK BUTTONS (zero third-party deps) ===
  return (
    <div className="flex flex-col items-center gap-3">
      <div className="relative w-full max-w-[220px] bg-slate-800/80 rounded-xl overflow-hidden flex items-center justify-center"
        style={{ height: mode === 'selfie' ? 140 : 100 }}>
        {mode === 'selfie' ? (
          <div className="w-24 h-32 border-2 border-dashed border-emerald-400/50 rounded-full flex items-center justify-center">
            <ScanFace className="w-8 h-8 text-emerald-400/30" />
          </div>
        ) : (
          <div className="w-[85%] h-[70%] border-2 border-dashed border-amber-400/50 rounded-lg flex items-center justify-center">
            <CreditCard className="w-8 h-8 text-amber-400/30" />
          </div>
        )}
      </div>

      {mode === 'cedula' && (
        <p className="text-xs text-amber-300/70 text-center">Enfoca la cedula para que se vea completa</p>
      )}

      {cameraError && (
        <p className="text-xs text-red-400 text-center px-2" data-testid="camera-error">{cameraError}</p>
      )}

      {hasCameraAPI && (
        <Button onClick={startCamera} data-testid="open-camera-btn"
          className="w-full max-w-xs bg-emerald-600 hover:bg-emerald-700 text-white h-11 gap-2">
          <Camera className="w-5 h-5" />
          {mode === 'selfie' ? 'Abrir Camara (Selfie)' : 'Abrir Camara (Cedula)'}
        </Button>
      )}

      <button onClick={openFilePicker} data-testid="upload-photo-btn"
        className={hasCameraAPI
          ? "text-xs text-slate-400 underline underline-offset-2 hover:text-slate-300"
          : "w-full max-w-xs flex items-center justify-center gap-2 h-11 rounded-md bg-emerald-600 text-white font-medium text-sm"
        }>
        {hasCameraAPI ? 'O subir foto desde galeria' : <><Upload className="w-5 h-5" /> {mode === 'selfie' ? 'Subir Selfie' : 'Subir Foto Cedula'}</>}
      </button>
      <input ref={fileInputRef} type="file" accept="image/*"
        className="hidden" onChange={handleFileChange} data-testid="photo-file-input" />
    </div>
  );
}

// ========== MAIN PAGE ==========
export default function EnrolamientoPage() {
  // Restore step and rut from sessionStorage (images are not stored - too large)
  // On mobile, camera may reload the page; we restore the exact step so user doesn't go back to RUT
  const [step, setStep] = useState(() => {
    try {
      const s = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
      return s?.step || 1;
    } catch { return 1; }
  });
  const [rut, setRut] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem(STORAGE_KEY))?.rut || ''; } catch { return ''; }
  });
  const [nombre, setNombre] = useState('');
  const [apellido, setApellido] = useState('');
  const [fechaNacimiento, setFechaNacimiento] = useState('');
  const [selfieImage, setSelfieImage] = useState(null);
  const [cedulaImage, setCedulaImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [rutStatus, setRutStatus] = useState(null);
  const [rutMismatch, setRutMismatch] = useState(null);
  const [fechaNacModificada, setFechaNacModificada] = useState(false);
  const [fechaNacOcr, setFechaNacOcr] = useState('');
  const [editandoFechaNac, setEditandoFechaNac] = useState(false);
  const [fechaTexto, setFechaTexto] = useState('');

  const progressPercent = (Math.min(step, 4) / 4) * 100;

  // Persist only step + rut (lightweight, no images)
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ step, rut }));
    } catch { /* quota exceeded */ }
  }, [step, rut]);

  const handleRutChange = (e) => {
    const limpio = e.target.value.replace(/[^0-9kK]/g, '');
    if (limpio.length <= 9) setRut(formatRut(e.target.value));
  };

  const handleNextFromRut = async () => {
    const limpio = rut.replace(/[^0-9kK]/g, '');
    if (limpio.length < 8) { toast.error('Ingresa un RUT valido'); return; }
    if (!validarRut(rut)) { toast.error('El RUT ingresado no es valido'); return; }
    setRutStatus('checking');
    try {
      const res = await axios.get(`${API}/enrolamiento/verificar-rut/${normalizarRut(rut)}`);
      const data = res.data;
      if (data.puede_enrolar) { setRutStatus(null); setStep(2); }
      else if (data.puede_reenrolar) { setRutStatus({ ...data, tipo: 'puede_reenrolar' }); }
      else { setRutStatus({ ...data, tipo: 'debe_esperar' }); }
    } catch { setRutStatus(null); setStep(2); }
  };

  const handleReenrolar = () => { setRutStatus(null); setStep(2); };

  // After cédula accepted → OCR → check RUT match
  const handleNextFromCedula = async () => {
    if (!cedulaImage) { toast.error('Debes tomar una foto de la cedula'); return; }
    setOcrLoading(true);
    setRutMismatch(null);
    try {
      const res = await axios.post(`${API}/enrolamiento/extraer-cedula`, { foto_cedula_base64: cedulaImage });
      const data = res.data;
      if (data.success) {
        // Check RUT match FIRST
        if (data.rut) {
          const ocrNorm = normalizarRut(data.rut);
          const inputNorm = normalizarRut(rut);
          if (ocrNorm !== inputNorm) {
            setRutMismatch({ ingresado: rut, detectado: data.rut });
            setOcrLoading(false);
            setStep('rut_mismatch');
            return;
          }
        }
        // Pre-fill data
        if (data.nombre && !nombre) setNombre(data.nombre);
        if (data.apellidos && !apellido) setApellido(data.apellidos);
        if (data.fecha_nacimiento && !fechaNacimiento) {
          setFechaNacimiento(data.fecha_nacimiento);
          setFechaNacOcr(data.fecha_nacimiento);
        }
      }
    } catch { /* OCR failed silently */ }
    finally { setOcrLoading(false); }
    setStep(4);
  };

  const handleSubmit = async () => {
    if (!nombre.trim()) { toast.error('Ingresa tu nombre'); return; }
    setLoading(true); setResultado(null);
    const isReenrolamiento = rutStatus?.tipo === 'puede_reenrolar';
    try {
      const res = await axios.post(`${API}/enrolamiento/verificar`, {
        rut: normalizarRut(rut), nombre: nombre.trim(), apellido: apellido.trim() || null,
        fecha_nacimiento: fechaNacimiento || null, foto_cara_base64: selfieImage,
        foto_cedula_base64: cedulaImage, forzar_reenrolamiento: isReenrolamiento,
        fecha_nacimiento_modificada: fechaNacModificada,
        fecha_nacimiento_ocr: fechaNacOcr || null,
      });
      if (res.data.success) setResultado({ tipo: 'exito', data: res.data });
      else if (res.data.ya_enrolado) setResultado({ tipo: 'ya_enrolado', data: res.data });
      else setResultado({ tipo: 'error', data: res.data });
      setStep(5);
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Error en la verificacion';
      setResultado({ tipo: 'error', data: { error: msg } }); setStep(5);
    } finally { setLoading(false); }
  };

  const resetFlow = () => {
    setStep(1); setRut(''); setNombre(''); setApellido(''); setFechaNacimiento('');
    setSelfieImage(null); setCedulaImage(null); setResultado(null); setLoading(false);
    setRutStatus(null); setRutMismatch(null); setFechaNacModificada(false);
    setFechaNacOcr(''); setEditandoFechaNac(false); setFechaTexto('');
    sessionStorage.removeItem(STORAGE_KEY);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white flex flex-col" data-testid="enrolamiento-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-slate-950/90 backdrop-blur border-b border-slate-800 px-4 py-2.5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-emerald-400" />
            <span className="font-bold text-sm tracking-wide">KINOSIS</span>
          </div>
          <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-400" data-testid="step-badge">
            Paso {typeof step === 'number' ? Math.min(step, 4) : '?'} / 4
          </Badge>
        </div>
        <div className="max-w-lg mx-auto mt-2">
          <Progress value={progressPercent} className="h-1 bg-slate-800" data-testid="enrollment-progress" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-lg mx-auto px-4 py-4 w-full flex flex-col">

        {/* ===== STEP 1: RUT ===== */}
        {step === 1 && (
          <div className="flex-1 flex flex-col gap-5" data-testid="step-rut">
            <div className="text-center space-y-1.5">
              <div className="w-12 h-12 rounded-2xl bg-sky-500/10 flex items-center justify-center mx-auto">
                <CreditCard className="w-6 h-6 text-sky-400" />
              </div>
              <h1 className="text-lg font-bold">Enrolamiento de Identidad</h1>
              <p className="text-xs text-slate-400">Verifica tu identidad para participar en ligas y clubes.</p>
            </div>

            <Card className="bg-slate-900/80 border-slate-800 p-4 space-y-3">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">RUT</Label>
                <Input value={rut} onChange={handleRutChange} placeholder="12.345.678-9"
                  className="bg-slate-800 border-slate-700 text-white text-lg text-center tracking-wider h-11"
                  data-testid="rut-input" inputMode="numeric" disabled={rutStatus === 'checking'} />
              </div>

              {rutStatus?.tipo === 'debe_esperar' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3" data-testid="rut-debe-esperar">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-amber-400" />
                    <p className="font-medium text-amber-300 text-sm">Ya estas enrolado</p>
                  </div>
                  <p className="text-xs text-slate-400">
                    Debes esperar <strong className="text-amber-300">{rutStatus.dias_restantes} dias</strong> para actualizar.
                  </p>
                </div>
              )}

              {rutStatus?.tipo === 'puede_reenrolar' && (
                <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3 space-y-2" data-testid="rut-puede-reenrolar">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-sky-400" />
                    <p className="font-medium text-sky-300 text-sm">Ya estas enrolado</p>
                  </div>
                  <p className="text-xs text-slate-400">Puedes crear un nuevo enrolamiento.</p>
                  <Button onClick={handleReenrolar} className="w-full bg-sky-600 hover:bg-sky-700 text-white gap-2 h-9"
                    data-testid="reenrolar-btn">
                    <RotateCcw className="w-4 h-4" /> Nuevo Enrolamiento
                  </Button>
                </div>
              )}

              {!rutStatus?.tipo && (
                <Button onClick={handleNextFromRut}
                  disabled={rut.replace(/[^0-9kK]/g, '').length < 8 || rutStatus === 'checking'}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white h-10 gap-2" data-testid="rut-next-btn">
                  {rutStatus === 'checking'
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Verificando...</>
                    : <>Continuar <ArrowRight className="w-4 h-4" /></>}
                </Button>
              )}
            </Card>
          </div>
        )}

        {/* ===== STEP 2: SELFIE ===== */}
        {step === 2 && (
          <div className="flex-1 flex flex-col" data-testid="step-selfie">
            <div className="text-center space-y-1 mb-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center mx-auto">
                <ScanFace className="w-5 h-5 text-emerald-400" />
              </div>
              <h2 className="text-base font-bold">Toma una Selfie</h2>
              <p className="text-xs text-slate-400">Buena iluminacion, mirando a la camara.</p>
            </div>

            <PhotoErrorBoundary onAccept={setSelfieImage}>
              <PhotoCapture mode="selfie" finalImage={selfieImage}
                onAccept={setSelfieImage} onRetake={() => setSelfieImage(null)} />
            </PhotoErrorBoundary>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => { setStep(1); setRutStatus(null); }}
                className="flex-1 border-slate-700 text-slate-300 h-10" data-testid="selfie-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Atras
              </Button>
              {selfieImage && (
                <Button onClick={() => setStep(3)}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-10"
                  data-testid="selfie-next-btn">
                  Continuar <ArrowRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ===== STEP 3: CEDULA ===== */}
        {step === 3 && (
          <div className="flex-1 flex flex-col" data-testid="step-cedula">
            <div className="text-center space-y-1 mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center mx-auto">
                <CreditCard className="w-5 h-5 text-amber-400" />
              </div>
              <h2 className="text-base font-bold">Foto de tu Cedula</h2>
              <p className="text-xs text-slate-400">Frente de la cedula, que se vea foto y RUT.</p>
            </div>

            <PhotoErrorBoundary onAccept={setCedulaImage}>
              <PhotoCapture mode="cedula" finalImage={cedulaImage}
                onAccept={setCedulaImage} onRetake={() => setCedulaImage(null)} />
            </PhotoErrorBoundary>

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep(2)}
                className="flex-1 border-slate-700 text-slate-300 h-10" data-testid="cedula-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Atras
              </Button>
              {cedulaImage && (
                <Button onClick={handleNextFromCedula} disabled={ocrLoading}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white gap-2 h-10" data-testid="cedula-next-btn">
                  {ocrLoading
                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Leyendo...</>
                    : <>Continuar <ArrowRight className="w-4 h-4" /></>}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* ===== RUT MISMATCH REJECTION ===== */}
        {step === 'rut_mismatch' && rutMismatch && (
          <div className="flex-1 flex flex-col justify-center gap-5" data-testid="step-rut-mismatch">
            <div className="text-center space-y-3">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-red-400" />
              </div>
              <h2 className="text-lg font-bold text-red-400">RUT No Coincide</h2>
              <p className="text-sm text-slate-400">
                El RUT ingresado no coincide con el RUT detectado en la cedula.
              </p>
            </div>

            <Card className="bg-slate-900/80 border-red-500/30 p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">RUT ingresado</span>
                <span className="font-mono font-bold text-white">{rutMismatch.ingresado}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">RUT en cedula</span>
                <span className="font-mono font-bold text-red-400">{rutMismatch.detectado}</span>
              </div>
            </Card>

            <p className="text-xs text-slate-500 text-center">
              Verifica que el RUT sea correcto y que la foto de la cedula sea clara.
            </p>

            <Button onClick={resetFlow} className="w-full bg-sky-600 hover:bg-sky-700 text-white h-10 gap-2"
              data-testid="mismatch-retry-btn">
              <RotateCcw className="w-4 h-4" /> Intentar Nuevamente
            </Button>
          </div>
        )}

        {/* ===== STEP 4: CONFIRM DATA ===== */}
        {step === 4 && (
          <div className="flex-1 flex flex-col gap-4" data-testid="step-datos">
            <div className="text-center space-y-1">
              <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center mx-auto">
                <User className="w-5 h-5 text-violet-400" />
              </div>
              <h2 className="text-base font-bold">Confirma tus Datos</h2>
              <p className="text-xs text-slate-400">Verifica o corrige la informacion.</p>
            </div>

            <Card className="bg-slate-900/80 border-slate-800 p-4 space-y-3">
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">RUT</Label>
                <Input value={rut} disabled className="bg-slate-800/50 border-slate-700 text-slate-400 text-center h-9" data-testid="confirm-rut" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Nombre *</Label>
                <Input value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Tu nombre"
                  className="bg-slate-800 border-slate-700 text-white h-9" data-testid="nombre-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Apellido</Label>
                <Input value={apellido} onChange={(e) => setApellido(e.target.value)} placeholder="Tu apellido"
                  className="bg-slate-800 border-slate-700 text-white h-9" data-testid="apellido-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-slate-300 text-xs">Fecha de Nacimiento</Label>
                {!editandoFechaNac ? (
                  <div className="flex items-center gap-2">
                    <Input value={formatDateDisplay(fechaNacimiento)} disabled
                      className="bg-slate-800/50 border-slate-700 text-slate-300 h-9 flex-1" data-testid="fecha-nacimiento-display" />
                    <Button type="button" variant="ghost" size="sm"
                      onClick={() => {
                        setEditandoFechaNac(true);
                        setFechaTexto(formatDateDisplay(fechaNacimiento));
                      }}
                      className="text-amber-400 hover:text-amber-300 text-xs px-2 h-9 shrink-0"
                      data-testid="modificar-fecha-btn">
                      Modificar
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Input value={fechaTexto} onChange={(e) => {
                      let val = e.target.value.replace(/[^\d-]/g, '');
                      const soloDigitos = val.replace(/-/g, '');
                      if (soloDigitos.length <= 8) {
                        let formatted = soloDigitos;
                        if (soloDigitos.length > 2) formatted = soloDigitos.slice(0, 2) + '-' + soloDigitos.slice(2);
                        if (soloDigitos.length > 4) formatted = soloDigitos.slice(0, 2) + '-' + soloDigitos.slice(2, 4) + '-' + soloDigitos.slice(4);
                        setFechaTexto(formatted);
                        if (soloDigitos.length === 8) {
                          const dd = soloDigitos.slice(0, 2);
                          const mm = soloDigitos.slice(2, 4);
                          const yyyy = soloDigitos.slice(4, 8);
                          const isoDate = `${yyyy}-${mm}-${dd}`;
                          const testDate = new Date(parseInt(yyyy), parseInt(mm) - 1, parseInt(dd));
                          if (testDate.getDate() === parseInt(dd) && testDate.getMonth() === parseInt(mm) - 1 && parseInt(yyyy) >= 1920 && parseInt(yyyy) <= 2025) {
                            setFechaNacimiento(isoDate);
                            setFechaNacModificada(isoDate !== fechaNacOcr);
                          }
                        }
                      }
                    }}
                      placeholder="dd-MM-yyyy" inputMode="numeric" maxLength={10}
                      className="bg-slate-800 border-amber-500/50 text-white h-9 text-center tracking-wider" data-testid="fecha-nacimiento-input" />
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2">
                      <p className="text-xs text-amber-300">Escribe la fecha en formato dd-MM-yyyy. Al modificar, tu enrolamiento quedara pendiente de verificacion manual.</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 pt-1">
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">Selfie</p>
                  {selfieImage && <img src={selfieImage} alt="Selfie" className="w-full h-16 object-cover rounded border border-slate-700" />}
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 mb-1">Cedula</p>
                  {cedulaImage && <img src={cedulaImage} alt="Cedula" className="w-full h-16 object-cover rounded border border-slate-700" />}
                </div>
              </div>
            </Card>

            <div className="flex gap-3 mt-auto">
              <Button variant="outline" onClick={() => setStep(3)}
                className="flex-1 border-slate-700 text-slate-300 h-10" data-testid="datos-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" /> Atras
              </Button>
              <Button onClick={handleSubmit} disabled={loading || !nombre.trim()}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 h-10" data-testid="submit-enrollment-btn">
                {loading
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Verificando...</>
                  : <><Shield className="w-4 h-4" /> Verificar</>}
              </Button>
            </div>
          </div>
        )}

        {/* ===== STEP 5: RESULT ===== */}
        {step === 5 && resultado && (
          <div className="flex-1 flex flex-col justify-center gap-5" data-testid="step-resultado">
            {resultado.tipo === 'exito' && (
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="text-lg font-bold text-emerald-400">
                  {resultado.data.es_reenrolamiento ? 'Re-enrolamiento Exitoso' : 'Enrolamiento Exitoso'}
                </h2>
                <Card className="bg-slate-900/80 border-emerald-500/30 p-4 space-y-2 text-left text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">RUT</span><span className="font-medium">{resultado.data.rut}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Nombre</span><span className="font-medium">{resultado.data.nombre} {resultado.data.apellido || ''}</span></div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Facial</span>
                    <Badge className={resultado.data.verificacion_facial ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                      {resultado.data.verificacion_facial ? 'Verificado' : 'Pendiente'}
                    </Badge>
                  </div>
                  {resultado.data.estado_verificacion === 'pendiente_verificacion_manual' && (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-2.5 mt-2">
                      <p className="text-xs text-amber-300 font-medium">Verificacion manual pendiente</p>
                      <p className="text-xs text-slate-400 mt-1">Tu enrolamiento sera revisado por un administrador ya que modificaste la fecha de nacimiento.</p>
                    </div>
                  )}
                </Card>
              </div>
            )}

            {resultado.tipo === 'ya_enrolado' && (
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-sky-500/20 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-sky-400" />
                </div>
                <h2 className="text-lg font-bold text-sky-400">Ya estas Enrolado</h2>
                <p className="text-sm text-slate-400">
                  {resultado.data.dias_restantes > 0
                    ? `Espera ${resultado.data.dias_restantes} dias para actualizar.`
                    : 'Puedes actualizar tu enrolamiento.'}
                </p>
              </div>
            )}

            {resultado.tipo === 'error' && (
              <div className="text-center space-y-3">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-lg font-bold text-red-400">Verificacion Fallida</h2>
                <p className="text-sm text-slate-400">{resultado.data.error}</p>
              </div>
            )}

            <Button onClick={resetFlow} variant="outline"
              className="w-full border-slate-700 text-slate-300 h-10 mt-auto" data-testid="reset-enrollment-btn">
              <RotateCcw className="w-4 h-4 mr-2" />
              {resultado.tipo === 'error' ? 'Intentar Nuevamente' : 'Nuevo Enrolamiento'}
            </Button>
          </div>
        )}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50" data-testid="loading-overlay">
          <div className="text-center space-y-3 p-8">
            <Loader2 className="w-10 h-10 text-emerald-400 animate-spin mx-auto" />
            <p className="text-white font-medium text-sm">Verificando identidad...</p>
          </div>
        </div>
      )}
    </div>
  );
}
